/**
 * Merkezin öğretmenleri.
 *
 * Buradaki tek önemli tasarım kararı ARAMA: aynı sınıftan ikinci
 * öğrenci geldiğinde uygulayıcının öğretmeni yeniden EKLEMESİ değil
 * BULMASI gerekiyor. Aynı e-posta ikinci kez eklenemiyor (veritabanı
 * engelliyor); arama o duvara çarpmayı baştan önlüyor.
 *
 * Öğretmen bir "kullanıcı" değil: paneli göremiyor, rolü yok. Kendi
 * adresinden giriyor ve yalnızca atandığı öğrencileri görüyor.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  ApiHatasi,
  OGRETMEN_ADRESI,
  OGRETMEN_TURU_ADI,
  ogretmenApi,
  type OgretmenDetayi,
  type OgretmenSatiri,
  type OgretmenTuru,
} from '../api'

const gun = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('tr-TR', { dateStyle: 'medium' }) : '—'

export function Ogretmenler({
  yazabilir,
  onDanisan,
}: {
  yazabilir: boolean
  onDanisan: (id: string) => void
}) {
  const [satirlar, setSatirlar] = useState<OgretmenSatiri[] | null>(null)
  const [ara, setAra] = useState('')
  const [tur, setTur] = useState<OgretmenTuru | ''>('')
  const [secili, setSecili] = useState<string | null>(null)
  const [ekleAcik, setEkleAcik] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  const yukle = useCallback(async () => {
    setHata(null)
    try {
      const r = await ogretmenApi.liste(ara.trim() || undefined, tur || undefined)
      setSatirlar(r.ogretmenler)
    } catch {
      setHata('Liste alınamadı.')
      setSatirlar([])
    }
  }, [ara, tur])

  useEffect(() => {
    void yukle()
  }, [yukle])

  if (secili) {
    return (
      <OgretmenSayfasi
        id={secili}
        yazabilir={yazabilir}
        onGeri={() => {
          setSecili(null)
          void yukle()
        }}
        onDanisan={onDanisan}
      />
    )
  }

  return (
    <>
      <h1>Okul tarafı</h1>
      <p className="alt">
        Öğretmen ve rehber öğretmenler. Her biri yalnızca atandığı öğrencileri görüyor; dosya,
        rapor ve ölçüm sonuçları okula açılmıyor.
      </p>

      {hata && <div className="uyari">{hata}</div>}

      <div className="dugmeler" style={{ marginBottom: 12 }}>
        <input
          placeholder="Ad, okul veya e-posta ara"
          value={ara}
          onChange={(e) => setAra(e.target.value)}
          style={{ minWidth: 240 }}
        />
        <select value={tur} onChange={(e) => setTur(e.target.value as OgretmenTuru | '')}>
          <option value="">Hepsi</option>
          <option value="teacher">Öğretmen</option>
          <option value="counselor">Rehber öğretmen</option>
        </select>
        {yazabilir && (
          <button className="birincil" onClick={() => setEkleAcik((x) => !x)}>
            {ekleAcik ? 'Vazgeç' : 'Öğretmen ekle'}
          </button>
        )}
      </div>

      {ekleAcik && (
        <OgretmenEkle
          onKayit={(id) => {
            setEkleAcik(false)
            setSecili(id)
          }}
        />
      )}

      {satirlar === null ? (
        <p className="alt">Yükleniyor…</p>
      ) : satirlar.length === 0 ? (
        <div className="bilgi">
          {ara || tur
            ? 'Bu aramaya uyan kayıt yok.'
            : 'Henüz öğretmen eklenmemiş. Bir öğrenci için okulun görüşü isteniyorsa buradan ekleyin.'}
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Ad</th>
              <th>Tür</th>
              <th>Okul</th>
              <th>E-posta</th>
              <th style={{ textAlign: 'right' }}>Öğrenci</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {satirlar.map((o) => (
              <tr key={o.id} style={o.arsiv ? { opacity: 0.55 } : undefined}>
                <td>
                  {o.adSoyad}
                  {o.brans ? <span className="alt"> · {o.brans}</span> : null}
                </td>
                <td>{OGRETMEN_TURU_ADI[o.tur]}</td>
                <td>{o.okul ?? '—'}</td>
                <td style={{ color: 'var(--muted)' }}>{o.eposta}</td>
                <td style={{ textAlign: 'right' }}>{o.ogrenci}</td>
                <td style={{ textAlign: 'right' }}>
                  <button onClick={() => setSecili(o.id)}>Aç</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

/* ------------------------------------------------------------------ */

function OgretmenEkle({ onKayit }: { onKayit: (id: string) => void }) {
  const [adSoyad, setAdSoyad] = useState('')
  const [tur, setTur] = useState<OgretmenTuru>('teacher')
  const [okul, setOkul] = useState('')
  const [brans, setBrans] = useState('')
  const [eposta, setEposta] = useState('')
  const [telefon, setTelefon] = useState('')
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  const kaydet = async (e: React.FormEvent) => {
    e.preventDefault()
    setHata(null)
    setBekliyor(true)
    try {
      const r = await ogretmenApi.ekle({
        adSoyad: adSoyad.trim(),
        tur,
        okul: okul.trim() || undefined,
        brans: brans.trim() || undefined,
        eposta: eposta.trim(),
        telefon: telefon.trim() || undefined,
      })
      onKayit(r.ogretmen.id)
    } catch (x) {
      setHata(
        x instanceof ApiHatasi && x.kod === 'eposta_kayitli'
          ? 'Bu e-posta merkezde zaten kayıtlı. Yeni kayıt açmak yerine listeden bulup öğrenci ekleyin.'
          : 'Kaydedilemedi.',
      )
    } finally {
      setBekliyor(false)
    }
  }

  return (
    <form className="kart" onSubmit={kaydet}>
      <h2 style={{ margin: '0 0 10px' }}>Yeni öğretmen</h2>
      {hata && <div className="uyari">{hata}</div>}

      <div className="satir">
        <label>
          Ad soyad
          <input value={adSoyad} onChange={(e) => setAdSoyad(e.target.value)} required minLength={2} />
        </label>
        <label>
          Tür
          <select value={tur} onChange={(e) => setTur(e.target.value as OgretmenTuru)}>
            <option value="teacher">Öğretmen</option>
            <option value="counselor">Rehber öğretmen</option>
          </select>
        </label>
        <label>
          Okul
          <input value={okul} onChange={(e) => setOkul(e.target.value)} />
        </label>
        <label>
          Branş
          <input value={brans} onChange={(e) => setBrans(e.target.value)} />
        </label>
        <label>
          E-posta (giriş kimliği)
          <input type="email" value={eposta} onChange={(e) => setEposta(e.target.value)} required />
        </label>
        <label>
          Telefon
          <input value={telefon} onChange={(e) => setTelefon(e.target.value)} />
        </label>
      </div>

      <p className="alt">
        Giriş kodu bu e-postaya gidiyor. Öğretmen{' '}
        <strong>{OGRETMEN_ADRESI[tur]}.nirengi</strong> adresinden giriyor ve ancak bir öğrenci
        atandıktan sonra içeride bir şey görüyor.
      </p>
      <button className="birincil" type="submit" disabled={bekliyor}>
        {bekliyor ? 'Kaydediliyor…' : 'Kaydet'}
      </button>
    </form>
  )
}

/* ------------------------------------------------------------------ */

function OgretmenSayfasi({
  id,
  yazabilir,
  onGeri,
  onDanisan,
}: {
  id: string
  yazabilir: boolean
  onGeri: () => void
  onDanisan: (id: string) => void
}) {
  const [d, setD] = useState<OgretmenDetayi | null>(null)
  const [hata, setHata] = useState<string | null>(null)

  const yukle = useCallback(async () => {
    setHata(null)
    try {
      setD(await ogretmenApi.detay(id))
    } catch {
      setHata('Kayıt açılamadı.')
    }
  }, [id])

  useEffect(() => {
    void yukle()
  }, [yukle])

  if (hata) return <div className="uyari">{hata}</div>
  if (!d) return <p className="alt">Yükleniyor…</p>

  const o = d.ogretmen

  return (
    <>
      <div className="dugmeler" style={{ marginBottom: 12 }}>
        <button onClick={onGeri}>← Okul tarafı</button>
      </div>

      <h1>{o.adSoyad}</h1>
      <p className="alt">
        {o.turAdi}
        {o.okul ? ` · ${o.okul}` : ''}
        {o.brans ? ` · ${o.brans}` : ''} · {o.eposta}
        {o.telefon ? ` · ${o.telefon}` : ''}
      </p>

      {o.arsiv && (
        <div className="bilgi">
          Bu kayıt arşivlendi. Giriş yapamıyor ve atamaları kapatıldı. Doldurduğu formlar
          dosyalarda duruyor.
        </div>
      )}

      <div className="kart">
        <strong style={{ fontSize: 13, color: 'var(--muted)' }}>Giriş adresi</strong>
        <p style={{ margin: '6px 0 0' }}>
          {OGRETMEN_ADRESI[o.tur]}.nirengi — e-posta ile kod istiyor, parola yok.
        </p>
      </div>

      {o.icNot && (
        <div className="kart">
          <strong style={{ fontSize: 13, color: 'var(--muted)' }}>
            İç not (öğretmen görmüyor)
          </strong>
          <p style={{ margin: '6px 0 0', whiteSpace: 'pre-wrap' }}>{o.icNot}</p>
        </div>
      )}

      <h2>Öğrencileri</h2>
      {d.ogrenciler.length === 0 ? (
        <div className="bilgi">
          Atanmış öğrenci yok. Atama danışan dosyasından yapılıyor: dosyada “Okul tarafı”
          bölümünden bu öğretmeni seçin. Okulla paylaşım onamı olmayan dosyada atama
          yapılamıyor.
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Öğrenci</th>
              <th>Atandı</th>
              <th>Not</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {d.ogrenciler.map((x) => (
              <tr key={x.atamaId}>
                <td>
                  {x.ad} {x.soyad}
                </td>
                <td style={{ color: 'var(--muted)' }}>{gun(x.atandi)}</td>
                <td style={{ color: 'var(--muted)' }}>
                  {x.not ? `${gun(x.notTarih)} tarihli not var` : '—'}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <button onClick={() => onDanisan(x.danisanId)}>Dosyayı aç</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {yazabilir && !o.arsiv && (
        <div className="dugmeler" style={{ marginTop: 14 }}>
          <button
            onClick={async () => {
              await ogretmenApi.arsivle(o.id)
              onGeri()
            }}
          >
            Arşivle
          </button>
        </div>
      )}
    </>
  )
}
